import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../../lib/api';

import type {
  ApiKey,
  ChangePasswordInput,
  CreateApiKeyInput,
  CreateApiKeyResponse,
  ImpersonationStatus,
  MeProfile,
  MfaBackupCodesResponse,
  MfaEnrollResponse,
  MfaStatus,
  PasswordPolicy,
  ProfileInput,
  Session,
  UpdatePreferenceInput,
  UserPreference,
} from './types';

export const settingsKeys = {
  me: ['settings', 'me'] as const,
  preferences: ['settings', 'preferences'] as const,
  sessions: ['settings', 'sessions'] as const,
  mfa: ['settings', 'mfa'] as const,
  apiKeys: ['settings', 'apiKeys'] as const,
  impersonation: ['settings', 'impersonation'] as const,
  passwordPolicy: (workspaceId: string | 'system') =>
    ['settings', 'passwordPolicy', workspaceId] as const,
};

/* ----------------------------- Profile / me ----------------------------- */

export function useMeQuery() {
  return useQuery({
    queryKey: settingsKeys.me,
    queryFn: () => apiClient.get<MeProfile>('/api/iam/auth/me'),
  });
}

export function useUpdateProfileMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProfileInput) => apiClient.patch<MeProfile>('/api/iam/users/me', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.me });
    },
  });
}

/* ------------------------------ Preferences ----------------------------- */

export function useMyPreferencesQuery() {
  return useQuery({
    queryKey: settingsKeys.preferences,
    queryFn: () => apiClient.get<UserPreference>('/api/shell/preferences'),
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePreferenceInput) =>
      apiClient.patch<UserPreference>('/api/shell/preferences', input),
    onSuccess: (data) => {
      qc.setQueryData(settingsKeys.preferences, data);
    },
  });
}

/* -------------------------------- Sessions ------------------------------ */

export function useMySessionsQuery() {
  return useQuery({
    queryKey: settingsKeys.sessions,
    queryFn: () => apiClient.get<Session[]>('/api/iam/sessions'),
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ success: true }>(`/api/iam/sessions/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.sessions });
    },
  });
}

export function useRevokeOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post<{ success: true }>('/api/iam/sessions/revoke-others'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.sessions });
    },
  });
}

/* ----------------------------- Impersonation ---------------------------- */

export function useImpersonationStatusQuery() {
  return useQuery({
    queryKey: settingsKeys.impersonation,
    queryFn: () => apiClient.get<ImpersonationStatus>('/api/iam/impersonation/current'),
    // Cheap, read-only; keep it fresh so the banner appears/disappears promptly.
    staleTime: 30_000,
  });
}

export function useEndImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post<{ ended: boolean }>('/api/iam/impersonation/end'),
    onSuccess: () => {
      // Identity changed back to the actor — drop impersonation + me caches.
      // (ImpersonationGate also hard-reloads, but keep this correct standalone.)
      void qc.invalidateQueries({ queryKey: settingsKeys.impersonation });
      void qc.invalidateQueries({ queryKey: settingsKeys.me });
    },
  });
}

/* ---------------------------------- MFA --------------------------------- */

export function useMfaStatusQuery() {
  return useQuery({
    queryKey: settingsKeys.mfa,
    queryFn: () => apiClient.get<MfaStatus>('/api/iam/mfa/status'),
  });
}

export function useEnrollMfaMutation() {
  return useMutation({
    mutationFn: (input: { userId: string; label: string }) =>
      apiClient.post<MfaEnrollResponse>('/api/iam/mfa/enroll', input),
  });
}

export function useVerifyMfaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; secret: string; code: string }) =>
      apiClient.post<{ valid: boolean }>('/api/iam/mfa/verify', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.mfa });
    },
  });
}

export function useGenerateBackupCodesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string }) =>
      apiClient.post<MfaBackupCodesResponse>('/api/iam/mfa/backup-codes', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.mfa });
    },
  });
}

export function useDisableMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete<{ success: true }>('/api/iam/mfa'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.mfa });
    },
  });
}

/* -------------------------------- API keys ------------------------------ */

export function useApiKeysQuery() {
  return useQuery({
    queryKey: settingsKeys.apiKeys,
    queryFn: () => apiClient.get<ApiKey[]>('/api/iam/api-keys'),
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApiKeyInput) =>
      apiClient.post<CreateApiKeyResponse>('/api/iam/api-keys', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.apiKeys });
    },
  });
}

export function useRotateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userPermissions }: { id: string; userPermissions: string[] }) =>
      apiClient.post<CreateApiKeyResponse>(`/api/iam/api-keys/${id}/rotate`, { userPermissions }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.apiKeys });
    },
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.delete<{ success: true }>(`/api/iam/api-keys/${id}`).then(() => ({ id, reason })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.apiKeys });
    },
  });
}

/* -------------------------- Password / policy --------------------------- */

export function usePasswordPolicyQuery(workspaceId: string | 'system' = 'system') {
  return useQuery({
    queryKey: settingsKeys.passwordPolicy(workspaceId),
    queryFn: () => apiClient.get<PasswordPolicy>(`/api/iam/password-policy/${workspaceId}`),
  });
}

export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      apiClient.post<{ success: true }>('/api/iam/auth/change-password', input),
  });
}
